import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Geolocation } from '@capacitor/geolocation';
import api from "../services/api";
import MapPicker from "../components/MapPicker";

function LocationSetup() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const forceManual = searchParams.get("change") === "true";

    const [status, setStatus] = useState(forceManual ? "manual" : "loading"); 
    const [position, setPosition] = useState(null);
    const [isFetchingLocation, setIsFetchingLocation] = useState(false);
    
    // State to hold addresses fetched from backend
    const [savedAddresses, setSavedAddresses] = useState([]);
    const hasAttempted = useRef(false);

    // 1. Fetch saved addresses on load
    useEffect(() => {
        const fetchSavedAddresses = async () => {
            try {
                const token = localStorage.getItem("token");
                if (!token) return;
                
                // FIXED: was "/addresses" (missing trailing slash). Your backend
                // route is @router.get("/") under prefix "/addresses", so the
                // real endpoint is "/addresses/". Without the slash, FastAPI
                // issues a redirect that can silently drop the Authorization
                // header / get blocked by CORS - causing this to always fail
                // quietly and leave savedAddresses empty.
                const response = await api.get("/addresses/", { 
                    headers: { Authorization: `Bearer ${token}` } 
                });
                setSavedAddresses(response.data);
            } catch (error) { 
                console.log("Failed to fetch saved addresses:", error); 
            }
        };
        fetchSavedAddresses();

        if (hasAttempted.current) return;
        hasAttempted.current = true;

        if (!forceManual) {
            attemptAutoLocation();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const reverseGeocode = async (lat, lng) => {
        try {
            const response = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`);
            const data = await response.json();
            if (data.results && data.results.length > 0) return data.results[0].formatted_address;
        } catch (error) { console.log(error); }
        return "Unknown Location";
    };

    // 2. Apply location for browsing only (doesn't save to their address book)
    const applySessionLocation = async (lat, lng, preFetchedLabel = null) => {
        setStatus("saving");
        const label = preFetchedLabel || await reverseGeocode(lat, lng);
        
        const locationData = { latitude: lat, longitude: lng, label };
        localStorage.setItem("browse_location", JSON.stringify(locationData));

        try {
            const token = localStorage.getItem("token");
            await api.put("/users/update-location", locationData, { 
                headers: { Authorization: `Bearer ${token}` } 
            });
        } catch (error) { console.log(error); }
        
        navigate("/home");
    };

    const attemptAutoLocation = async () => {
        try {
            const perm = await Geolocation.requestPermissions();
            if (perm.location !== 'granted') { setStatus("manual"); return; }
            const location = await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 10000 });
            setPosition([location.coords.latitude, location.coords.longitude]);
            await applySessionLocation(location.coords.latitude, location.coords.longitude);
        } catch (error) { setStatus("manual"); }
    };

    const handleUseCurrentLocation = async () => {
    setIsFetchingLocation(true);
    try {
        const perm = await Geolocation.requestPermissions();
        if (perm.location !== 'granted') { 
            alert("Location permission denied. Please allow in settings."); 
            return; 
        }
        
        const location = await Geolocation.getCurrentPosition({ 
            enableHighAccuracy: true, 
            timeout: 15000, 
            maximumAge: 30000  // accept a fix up to 30s old instead of forcing a brand-new one every time
        });
        setPosition([location.coords.latitude, location.coords.longitude]);
    } catch (error) { 
        console.log("Geolocation error:", error);
        if (error.code === 1) {
            alert("Location permission denied. Please allow in settings.");
        } else if (error.code === 3) {
            alert("Location request timed out. Please try again or check your GPS signal.");
        } else {
            alert("Couldn't get your location. Please make sure GPS is turned on.");
        }
    } finally { 
        setIsFetchingLocation(false); 
    }
};

    return (
        <div style={styles.root}>
            <div style={styles.card}>
                <h1 style={styles.title}>📍 Where should we deliver?</h1>

                {status === "loading" && <p style={styles.subtitle}>Finding your location…</p>}
                {status === "saving" && <p style={styles.subtitle}>Setting up your delivery area…</p>}

                {status === "manual" && (
                    <>
                        <p style={styles.subtitle}>
                            Choose a saved address or set your location on the map to see nearby restaurants.
                        </p>

                        {/* --- NEW: Display Saved Addresses for Quick Selection --- */}
                        {savedAddresses.length > 0 && (
                            <div style={styles.savedSection}>
                                <h3 style={styles.sectionHeading}>Your Saved Addresses</h3>
                                {savedAddresses.map(addr => (
                                    <div 
                                        key={addr.id} 
                                        style={styles.savedCard} 
                                        // When clicked, instantly use this for browsing
                                        onClick={() => applySessionLocation(addr.latitude, addr.longitude, addr.address_name)}
                                    >
                                        <span style={styles.savedIcon}>
                                            {addr.address_name.toLowerCase() === 'home' ? '🏠' : addr.address_name.toLowerCase() === 'work' ? '💼' : '📍'}
                                        </span>
                                        <div style={styles.savedInfo}>
                                            <h4 style={styles.savedLabel}>{addr.address_name}</h4>
                                            <p style={styles.savedText}>{addr.full_address}</p>
                                        </div>
                                    </div>
                                ))}
                                <div style={styles.divider}></div>
                            </div>
                        )}

                        <h3 style={styles.sectionHeading}>
                            {savedAddresses.length > 0 ? "Or choose on map" : "Choose on map"}
                        </h3>
                        
                        <button 
                            onClick={handleUseCurrentLocation} 
                            disabled={isFetchingLocation} 
                            style={{...styles.gpsBtn, opacity: isFetchingLocation ? 0.7 : 1}}
                        >
                            <span style={{ fontSize: '18px' }}>📍</span> 
                            {isFetchingLocation ? "Locating..." : "Use my current location"}
                        </button>

                        <div style={styles.mapWrap}>
                            <MapPicker position={position} onPositionChange={setPosition} />
                        </div>

                        <button 
                            style={{ ...styles.confirmBtn, opacity: !position ? 0.5 : 1 }} 
                            onClick={() => position && applySessionLocation(position[0], position[1])} 
                            disabled={!position}
                        >
                            Confirm Browsing Location
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}

const styles = {
    root: { minHeight: "100vh", display: "flex", justifyContent: "center", alignItems: "flex-start", background: "#f0f2f5", padding: "20px", boxSizing: "border-box" },
    card: { width: "100%", maxWidth: "480px", background: "#ffffff", borderRadius: "16px", padding: "24px", boxShadow: "0 2px 10px rgba(0,0,0,0.06)" },
    title: { fontSize: "20px", fontWeight: "800", color: "#1c1c1c", margin: "0 0 6px" },
    subtitle: { fontSize: "14px", color: "#7e7e7e", margin: "0 0 18px", lineHeight: 1.4 },
    mapWrap: { borderRadius: "12px", overflow: "hidden", marginBottom: "16px" },
    confirmBtn: { width: "100%", padding: "14px", background: "#ff6b00", color: "#fff", border: "none", borderRadius: "12px", fontWeight: "800", fontSize: "15px", cursor: "pointer", marginTop: "8px" },
    gpsBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', width: '100%', padding: '12px 16px', background: '#fff3eb', border: '1px solid #ff6b00', borderRadius: '12px', color: '#ff6b00', fontSize: '15px', fontWeight: '700', cursor: 'pointer', marginBottom: '16px' },
    
    // Saved Addresses Styles
    savedSection: { marginBottom: "10px" },
    sectionHeading: { fontSize: "15px", fontWeight: "800", color: "#1c1c1c", margin: "0 0 12px 0" },
    savedCard: { display: "flex", gap: "14px", padding: "14px", background: "#f8f9fa", borderRadius: "12px", marginBottom: "12px", cursor: "pointer", border: "1.5px solid #ececef", alignItems: "center", transition: "border 0.2s" },
    savedIcon: { fontSize: "22px" },
    savedInfo: { flex: 1, minWidth: 0 },
    savedLabel: { fontSize: "14px", fontWeight: "800", color: "#1c1c1c", margin: "0 0 4px 0" },
    savedText: { fontSize: "12px", color: "#7e7e7e", margin: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
    divider: { height: "1.5px", background: "#ececef", margin: "24px 0 20px 0" },
};

export default LocationSetup;